<template>
  <section v-show="active" class="app-modal__section">
    <header class="app-modal__section-head">
      <h4>{{ text.heading.title }}</h4>
      <p>{{ text.heading.description }}</p>
    </header>

    <div class="billing-status-row">
      <label class="app-modal-toggle billing-toggle" :class="{ 'app-modal-toggle--disabled': disabled }">
        <input
          type="checkbox"
          :checked="billingStatus === 'unbilled'"
          :disabled="disabled"
          @change="setBillingStatus('unbilled')"
        />
        <span>{{ text.status.unbilled }}</span>
      </label>

      <label class="app-modal-toggle billing-toggle" :class="{ 'app-modal-toggle--disabled': disabled }">
        <input
          type="checkbox"
          :checked="billingStatus === 'orderly'"
          :disabled="disabled"
          @change="setBillingStatus('orderly')"
        />
        <span>{{ text.status.orderly }}</span>
      </label>

      <label class="app-modal-toggle billing-toggle" :class="{ 'app-modal-toggle--disabled': disabled }">
        <input
          type="checkbox"
          :checked="billingStatus === 'external'"
          :disabled="disabled"
          @change="setBillingStatus('external')"
        />
        <span>{{ text.status.external }}</span>
      </label>
    </div>

    <div v-if="billingStatus === 'orderly'" class="app-modal__grid two-columns billing-detail-row">
      <div class="app-modal-field full-width">
        <span>{{ text.fields.invoice }}</span>
        <multiselect
          v-model="form.orderlyInvoice"
          :options="options.invoices"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="true"
          :allow-empty="true"
          :disabled="disabled"
          class="app-modal-select"
          :placeholder="text.fields.invoicePlaceholder"
        >
          <template #noResult><span>{{ commonText.noOption }}</span></template>
          <template #noOptions><span>{{ commonText.noOption }}</span></template>
        </multiselect>
      </div>
    </div>

    <div v-if="billingStatus === 'external'" class="app-modal__grid two-columns billing-detail-row">
      <div class="app-modal-field full-width">
        <span>{{ text.fields.invoiceXml }}</span>
        <div class="expense-file-upload">
          <input
            ref="invoiceXmlInput"
            type="file"
            class="expense-file-upload__input"
            accept="text/xml,application/xml,.xml"
            :disabled="disabled"
            @change="onXmlFileChange"
          />
          <button
            type="button"
            class="app-header-btn app-header-btn--secondary expense-file-upload__trigger"
            :disabled="disabled"
            @click="openFilePicker"
          >
            {{ text.actions.uploadXml }}
          </button>
          <span class="expense-file-upload__name">{{ resolveFileName(form.invoiceXmlFile) }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { PAYMENTS_TEXT } from '@/texts/catalog/payments';

const BILLING_TEXT = PAYMENTS_TEXT.planModal.billing;
const COMMON_TEXT = PAYMENTS_TEXT.planModal.common;
const FALLBACK_TEXT = PAYMENTS_TEXT.planModal.fallback;

export default {
  name: 'PaymentPlanModalSectionBilling',
  data() {
    return {
      text: BILLING_TEXT,
      commonText: COMMON_TEXT,
      fallbackText: FALLBACK_TEXT,
    };
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    form: {
      type: Object,
      required: true,
    },
    options: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    billingStatus() {
      const status = String(this.form?.billingStatus || '').trim().toLowerCase();
      if (status === 'orderly' || status === 'external') {
        return status;
      }

      return 'unbilled';
    },
  },
  methods: {
    setBillingStatus(nextStatus) {
      if (this.disabled) {
        return;
      }

      const status = String(nextStatus || '').trim().toLowerCase();
      if (!['unbilled', 'orderly', 'external'].includes(status)) {
        return;
      }

      this.form.billingStatus = status;

      if (status !== 'orderly') {
        this.form.orderlyInvoice = null;
        this.form.orderlyInvoiceId = null;
      }

      if (status !== 'external') {
        this.form.externalInvoiceReference = '';
        this.form.invoiceXmlFile = null;
      }
    },
    openFilePicker() {
      if (this.disabled) {
        return;
      }

      const input = this.$refs.invoiceXmlInput;
      if (input && typeof input.click === 'function') {
        input.click();
      }
    },
    onXmlFileChange(event) {
      const file = event?.target?.files?.[0] || null;
      this.form.invoiceXmlFile = file;
    },
    resolveFileName(file) {
      return file?.name || this.fallbackText.noFileSelected;
    },
  },
};
</script>

<style scoped>
.billing-status-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.billing-toggle {
  min-height: 42px;
  border: 1px solid rgba(153, 152, 153, 0.3);
  border-radius: 8px;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  margin: 0;
}

.billing-detail-row {
  margin-top: 10px;
}

.expense-file-upload {
  border: 1px solid rgba(153, 152, 153, 0.3);
  border-radius: 8px;
  min-height: 44px;
  padding: 6px 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: #ffffff;
}

.expense-file-upload__input {
  display: none;
}

.expense-file-upload__trigger {
  min-height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 12px;
  flex-shrink: 0;
}

.expense-file-upload__name {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  font-size: 12px;
  color: #4b5563;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 900px) {
  .billing-status-row {
    grid-template-columns: 1fr;
  }
}
</style>
